import { CaptureTreeRevision, ScanEntity } from "@custom-types/capture-tree/capture-tree-types";
import { ExistingFilesToReuse } from "@custom-types/file-upload-types";
import { GUID } from "@faro-lotv/foundation";
import { CaptureApiClient, CaptureTreeEntity, CaptureTreeEntityType, CaptureTreePointCloudType, RegistrationState, RevisionType } from "@faro-lotv/service-wires";
import { AppDispatch } from "@store/store-helper";
import { uploadedFileHash } from "@utils/file-utils";
import { fetchRevisionsAndDraftEntities } from "@hooks/data-management/use-data-management";
import { isScanEntity } from "@utils/capture-tree/capture-tree-utils";
import { sentryCaptureError } from "@utils/sentry-utils";
import { ProjectApi } from "@api/project-api/project-api";

type ReuseInfo = {
  /** Unmerged revisions of same user, client & draft. */
  unmergedRevisions: CaptureTreeRevision[],
  /** Matching ScanEntities and their file infos. */
  existingFilesToReuse?: ExistingFilesToReuse<ScanEntity>;
}

/**
 * When re-using entities from an old revision, clean up all open revisions of the same user & client & draft
 * if they weren't modified in the last minute.
 */
const REVISION_CLEANUP_MIN_AGE_REUSE_MIN = 1;
/** When not re-using entities, be more careful, and check if modified in last 30 minutes. */
const REVISION_CLEANUP_MIN_AGE_NO_REUSE_MIN = 30;

// -----------------------------------------------------------------------------------------------

/**
 * @param dispatch Store dispatch function.
 * @param projectApiClient Project API client.
 * @param uploadRevisionId Our current upload revision.
 * @param filesToUpload Files that the user wants to upload (and which are not yet present in the Draft revision).
 * @param currentUserId User ID.
 * @returns unmergedRevisions - Open or canceled revisions of same user, client & draft. Candidates for canceling.
 *          existingFilesToReuse - Matching ScanEntities and their file infos, found in the previous revisions.
 */
export async function getMapOfScanEntitiesForReuse(
  dispatch: AppDispatch,
  projectApiClient: ProjectApi,
  uploadRevisionId: GUID,
  filesToUpload: File[],
  currentUserId: string
): Promise<ReuseInfo> {
  // Check which files are interesting.
  // We'll only add matching files to the map.
  const glsFilesHashes = new Set<string>(
    filesToUpload.map((file) => uploadedFileHash(file.name, file.size))
  );

  const { openDraftRevision, revisions } = await fetchRevisionsAndDraftEntities(
    dispatch, projectApiClient
  );
  if (!openDraftRevision) {
    return { unmergedRevisions: [], existingFilesToReuse: undefined };
  }

  const { started, registered, canceled } = RegistrationState;
  const unmergedRevisions = revisions.filter((revision) =>
    revision.createdBy === currentUserId &&
    revision.sourceRevisionId === openDraftRevision.id &&
    revision.revisionType !== RevisionType.draft &&
    (revision.state === started || revision.state === registered || revision.state === canceled) &&
    revision.createdByClient === CaptureApiClient.dashboard &&
    // We should neither borrow entities from our own revision, nor cancel it.
    revision.id !== uploadRevisionId
  );
  if (unmergedRevisions.length === 0) {
    return { unmergedRevisions: [], existingFilesToReuse: undefined };
  }

  // Sort to get latest modified first:
  unmergedRevisions.sort((a, b) => {
    const aModified = new Date(a.modifiedAt || a.createdAt || 0).getTime();
    const bModified = new Date(b.modifiedAt || b.createdAt || 0).getTime();
    return bModified - aModified;
  });

  // Take max. 4 revisions to not spend too much time & resources:
  const MAX_REVISIONS = 4;
  const unmergedRevisionsCapped = unmergedRevisions.slice(0, MAX_REVISIONS);

  // Get ScanEntities of these unmergedRevisions
  const revisionEntities = new Map<CaptureTreeRevision, CaptureTreeEntity[]>();
  await Promise.all(unmergedRevisionsCapped.map(async (revision) => {
    const entitiesThis = await projectApiClient.getCaptureTreeForRegistrationRevision(revision.id)
      .catch((error) => {
        // Non-essential, so just log the error silently.
        sentryCaptureError({ error, title: "getMapOfScanEntitiesForReuse", data: { revisionId: revision.id } });
        return [];
      });
    if (entitiesThis.length > 0) {
      revisionEntities.set(revision, entitiesThis);
    }
  }));

  // Extract all ElsRaw point cloud files from the scan entities, and then check which of them
  // match one of the `filesToUpload`.
  const existingFilesToReuse: ExistingFilesToReuse<ScanEntity> = {};
  const existingRevisionsToReuse = new Set<CaptureTreeRevision>();

  for (const [revision, entities] of revisionEntities.entries()) {
    for (const entity of entities) {
      if (!isScanEntity(entity) || entity.type !== CaptureTreeEntityType.elsScan || !entity.pointClouds?.length) {
        continue;
      }
      for (const pc of entity.pointClouds) {
        if (pc.type === CaptureTreePointCloudType.elsRaw && pc.fileName && pc.fileSize && pc.uri && pc.md5Hash) {
          const hash = uploadedFileHash(pc.fileName, pc.fileSize);
          if (glsFilesHashes.has(hash)) {
            existingFilesToReuse[hash] = {
              fileName: pc.fileName,
              fileSize: pc.fileSize,
              md5: pc.md5Hash,
              downloadUrl: pc.uri,
              data: entity,
            };
            existingRevisionsToReuse.add(revision);
          }
        }
      }
    }
  }
  return {
    unmergedRevisions,
    existingFilesToReuse: Object.keys(existingFilesToReuse).length > 0 ? existingFilesToReuse : undefined,
  };
}

/**
 * Cancel those of the given revisions that are not canceled or merged yet, and were not recently modified.
 * If a revision was recently modified, the upload might still be in progress.
 * @param projectApiClient
 * @param revisions Revisions that you consider safe to cancel (same user, client & draft = sourceRevisionId).
 */
export async function cancelOldOpenRevisions(
  projectApiClient: ProjectApi, revisions: CaptureTreeRevision[], isReusing: boolean
): Promise<void> {
  const minuteToMsec = 60_000;
  const minAgeMsec = (isReusing ? REVISION_CLEANUP_MIN_AGE_REUSE_MIN : REVISION_CLEANUP_MIN_AGE_NO_REUSE_MIN) * minuteToMsec;

  for (const revision of revisions) {
    const ageMsec = Date.now() - new Date(revision.modifiedAt || revision.createdAt || 0).getTime();
    if (
      revision.revisionType === RevisionType.draft ||
      revision.state === RegistrationState.canceled || revision.state === RegistrationState.merged ||
      ageMsec < minAgeMsec
    ) {
      continue;
    }

    await projectApiClient.updateRegistrationRevision({
      registrationRevisionId: revision.id,
      state: RegistrationState.canceled,
    }).catch((error) => {
      // Non-essential, so just log the error silently.
      sentryCaptureError({ error, title: "Failed to cancel orphaned revision", data: { revisionId: revision.id } });
    });
  }
}
